import { ColumnDef, createColumnHelper } from '@tanstack/react-table'
import dayjs from 'dayjs'
import { useRouter } from 'next/router'

import { DefinitionListItem } from 'src/components/atoms/DefinitionListItem'
import { InternalLink } from 'src/components/atoms/Link'
import { Tooltip } from 'src/components/atoms/Tooltip'
import { ParagraphNoSpace } from 'src/components/atoms/Typography'
import { TableNew } from 'src/components/molecules/Table'
import { TableSkeleton } from 'src/components/molecules/Table/TableNew'

import { Planting, Season } from 'src/config/generated/graphql'
import { viewApplicationPath, viewProjectDetailPath } from 'src/config/paths'
import useMediaQuery from 'src/hooks/useMediaQuery'
import {
  ApplicationWorkflowState,
  ListDashboardApplicationType
} from 'src/types/application'
import { logError } from 'src/utils/errors'

import { getNextStep, getPath, getStateName } from './helper'

export interface DashboardApplicationsTableProps {
  applicationsData: ListDashboardApplicationType[]
  isLoading?: boolean
}

export const DashboardApplicationsTable = ({
  applicationsData,
  isLoading
}: DashboardApplicationsTableProps) => {
  const router = useRouter()

  const columnHelper = createColumnHelper<ListDashboardApplicationType>()
  const mobileColumns: ColumnDef<ListDashboardApplicationType, any>[] = [
    columnHelper.accessor('Id', {
      cell: ({ row: { original } }) => {
        return (
          <div className='flex flex-col'>
            <dl className='mb-3 pb-2'>
              <DefinitionListItem title='Season' data={original.Season?.Name} />
              <DefinitionListItem
                title='Application'
                data={
                  original.Project?.Id ? (
                    <Tooltip
                      placement='left'
                      overlay={
                        <div className='w-56 px-[5px] text-center'>
                          <InternalLink
                            className='line-clamp-2 text-white! hover:underline font-normal!'
                            href={viewApplicationPath({
                              routeParams: {
                                applicationId: original.Id,
                                projectId: original.Project?.Id
                              }
                            })}
                            onClick={e => e.stopPropagation()}
                          >
                            Go to Application:{' '}
                            <span className='font-semibold'>
                              {original.Reference}
                            </span>
                          </InternalLink>
                        </div>
                      }
                      trigger={['click']}
                    >
                      <div
                        onClick={e => e.stopPropagation()}
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.stopPropagation()
                            e.preventDefault()
                          }
                        }}
                        role='button'
                        tabIndex={0}
                        className='line-clamp-2 text-mostlyGreen! hover:text-secondaryDark! font-normal! cursor-pointer'
                      >
                        {original.Reference}
                      </div>
                    </Tooltip>
                  ) : (
                    original.Reference
                  )
                }
              />
              <DefinitionListItem
                title='Project'
                data={
                  original.Project?.Id ? (
                    <Tooltip
                      placement='left'
                      overlay={
                        <div className='w-56 px-[5px] text-center'>
                          <InternalLink
                            className='line-clamp-2 text-white! hover:underline font-normal!'
                            href={viewProjectDetailPath({
                              routeParams: {
                                projectId: original.Project.Id
                              }
                            })}
                            onClick={e => e.stopPropagation()}
                          >
                            Go to Project:{' '}
                            <span className='font-semibold'>
                              {original.Project?.Name}
                            </span>
                          </InternalLink>
                        </div>
                      }
                      trigger={['click']}
                    >
                      <div
                        onClick={e => e.stopPropagation()}
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.stopPropagation()
                            e.preventDefault()
                          }
                        }}
                        role='button'
                        tabIndex={0}
                        className='line-clamp-2 text-mostlyGreen! hover:text-secondaryDark! font-normal! cursor-pointer'
                      >
                        {original.Project?.Name}
                      </div>
                    </Tooltip>
                  ) : (
                    '-'
                  )
                }
              />
              <DefinitionListItem
                title='Status'
                data={getStateName(
                  original.WorkflowState as ApplicationWorkflowState,
                  original.Season as Season,
                  original.Approved as number,
                  original.Plantings as Planting[]
                )}
              />
              <DefinitionListItem
                title='Trees Approved'
                data={original.Approved}
              />
              <DefinitionListItem
                title='Next Step'
                data={getNextStep(
                  original.WorkflowState as ApplicationWorkflowState,
                  original.Season as Season,
                  original.Approved as number,
                  original.Plantings as Planting[]
                )}
                className='border-b-0!'
              />
            </dl>
          </div>
        )
      }
    })
  ]

  const desktopColumns: ColumnDef<ListDashboardApplicationType, any>[] = [
    columnHelper.accessor('Season.Name', {
      size: 80,
      cell: props => {
        return <span>{props.getValue()}</span>
      },
      header: 'Season'
    }),
    columnHelper.accessor('Reference', {
      header: 'Application',
      size: 100,
      cell: ({ row: { original } }) => {
        return original.Project?.Id ? (
          <Tooltip
            placement='bottom'
            overlay={
              <div className='w-56 px-[5px] text-center'>
                <InternalLink
                  className='line-clamp-2 text-white! hover:underline font-normal!'
                  href={viewApplicationPath({
                    routeParams: {
                      applicationId: original.Id,
                      projectId: original.Project?.Id
                    }
                  })}
                  onClick={e => e.stopPropagation()}
                >
                  Go to Application:{' '}
                  <span className='font-semibold'>{original.Reference}</span>
                </InternalLink>
              </div>
            }
            trigger={['click']}
          >
            <div
              onClick={e => e.stopPropagation()}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.stopPropagation()
                  e.preventDefault()
                }
              }}
              role='button'
              tabIndex={0}
              className='line-clamp-2 text-mostlyGreen! hover:text-secondaryDark! font-normal! cursor-pointer'
            >
              {original.Reference}
            </div>
          </Tooltip>
        ) : (
          original.Reference
        )
      }
    }),
    columnHelper.accessor('Project.Id', {
      header: 'Project',
      size: 130,
      cell: ({ row: { original } }) =>
        original.Project?.Id ? (
          <Tooltip
            placement='bottom'
            overlay={
              <div className='w-56 px-[5px] text-center'>
                <InternalLink
                  className='line-clamp-2 text-white! hover:underline font-normal!'
                  href={viewProjectDetailPath({
                    routeParams: {
                      projectId: original.Project.Id
                    }
                  })}
                  onClick={e => e.stopPropagation()}
                >
                  Go to Project:{' '}
                  <span className='font-semibold'>
                    {original.Project?.Name}
                  </span>
                </InternalLink>
              </div>
            }
            trigger={['click']}
          >
            <div
              onClick={e => e.stopPropagation()}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.stopPropagation()
                  e.preventDefault()
                }
              }}
              role='button'
              tabIndex={0}
              className='line-clamp-2 text-mostlyGreen! hover:text-secondaryDark! font-normal! cursor-pointer'
            >
              {original.Project?.Name}
            </div>
          </Tooltip>
        ) : (
          '-'
        )
    }),
    columnHelper.accessor('WorkflowState', {
      size: 130,
      cell: props => (
        <span>
          {getStateName(
            props.row.original.WorkflowState as ApplicationWorkflowState,
            props.row.original.Season as Season,
            props.row.original.Approved as number,
            props.row.original.Plantings as Planting[]
          )}
        </span>
      ),
      header: 'Status'
    }),
    columnHelper.accessor('Approved', {
      cell: props => <span>{props.getValue()}</span>,
      size: 130,
      header: 'Trees Approved'
    }),
    columnHelper.accessor('Id', {
      size: 240,
      cell: props => {
        const {
          WorkflowState,
          Season: season,
          Approved,
          Plantings
        } = props.row.original
        const nextStep = getNextStep(
          WorkflowState as ApplicationWorkflowState,
          season as Season,
          Approved as number,
          Plantings as Planting[]
        )

        if (nextStep) {
          return nextStep
        }

        if (WorkflowState === ApplicationWorkflowState.Draft) {
          return (
            <ParagraphNoSpace>
              This tree request can no longer be submitted
            </ParagraphNoSpace>
          )
        }

        if (WorkflowState === ApplicationWorkflowState.Pending) {
          return (
            <ParagraphNoSpace className='text-ivoryBlack'>
              Under review{' '}
              <span className='text-steelGrey'>
                (until{' '}
                {dayjs(season?.SubmissionDueDate)
                  .add(1, 'month')
                  .endOf('month')
                  .format('D MMM YYYY')}
                )
              </span>
            </ParagraphNoSpace>
          )
        }

        return <ParagraphNoSpace>No action required</ParagraphNoSpace>
      },
      header: 'Next step'
    })
  ]

  const isMobile = useMediaQuery('(max-width: 1024px)')

  const columns = isMobile ? mobileColumns : desktopColumns

  return (
    <TableNew
      isLoading={isLoading}
      skeletonRows={
        isLoading ? <TableSkeleton rows={5} columns={6} /> : undefined
      }
      className='[&>thead]:sr-only lg:[&>thead]:not-sr-only'
      columns={columns}
      onSelect={item => {
        const path = getPath(
          item.Id,
          item.Project?.Id,
          item.WorkflowState as ApplicationWorkflowState,
          item.Season as Season
        )

        if (path) {
          router.push(path)
        } else {
          logError(
            'Planter dashboard navigation prevented: path is undefined or invalid'
          )
        }
      }}
      showSelectChevron
      isSelectable={item =>
        item.Project?.Id &&
        !!getNextStep(
          item.WorkflowState as ApplicationWorkflowState,
          item.Season as Season,
          item.Approved as number,
          item.Plantings as Planting[]
        )
      }
      data={isLoading ? [] : applicationsData || []}
      noResultsText="You haven't requested trees yet. Once you do, you'll be able to view and manage requests here."
      headerClasses='text-left! px-2 pt-0! pb-4!'
      cellClasses='text-left! px-2'
      enableSorting={false}
    />
  )
}
